import type { MultilingualQuestion } from '../../types/multilingualQuestion';

export const specializedDrivingMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-spd-1',
    text: {
      en: 'What is the maximum speed on a Dutch motorway when towing a trailer with a category B licence?',
      nl: 'Wat is de maximumsnelheid op een Nederlandse snelweg als je met een categorie B-rijbewijs een aanhanger trekt?',
      ar: 'ما هي السرعة القصوى على الطريق السريع الهولندي عند سحب مقطورة بترخيص الفئة B؟'
    },
    options: [
      {
        id: 'q-spd-1o1',
        text: {
          en: '100 km/h',
          nl: '100 km/u',
          ar: '100 كم/ساعة'
        }
      },
      {
        id: 'q-spd-1o2',
        text: {
          en: '120 km/h',
          nl: '120 km/u',
          ar: '120 كم/ساعة'
        }
      },
      {
        id: 'q-spd-1o3',
        text: {
          en: '90 km/h',
          nl: '90 km/u',
          ar: '90 كم/ساعة'
        }
      },
      {
        id: 'q-spd-1o4',
        text: {
          en: '80 km/h',
          nl: '80 km/u',
          ar: '80 كم/ساعة'
        }
      }
    ],
    correctAnswerId: 'q-spd-1o3',
    explanation: {
      en: 'When towing a trailer with a category B licence, the maximum speed on a Dutch motorway is 90 km/h — even if the motorway limit is higher. This applies regardless of trailer weight. (RVV 1990 art. 22)',
      nl: 'Als je met een categorie B-rijbewijs een aanhanger trekt, geldt op de snelweg een maximumsnelheid van 90 km/u — ook als de algemene snelheidslimiet hoger is. Dit geldt ongeacht het gewicht van de aanhanger. (RVV 1990 art. 22)',
      ar: 'عند سحب مقطورة بترخيص الفئة B، تكون السرعة القصوى على الطريق السريع الهولندي 90 كم/ساعة — حتى لو كان الحد الأقصى للطريق السريع أعلى. ينطبق هذا بغض النظر عن وزن المقطورة. (RVV 1990 المادة 22)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-2',
    text: {
      en: 'What is the maximum permitted mass of an unbraked trailer towed by a category B vehicle?',
      nl: 'Wat is de maximaal toegestane massa van een aanhanger zonder eigen remmen die door een categorie B-voertuig wordt getrokken?',
      ar: 'ما هي الكتلة المسموح بها للمقطورة غير المكبحة التي يسحبها مركبة من الفئة B؟'
    },
    options: [
      {
        id: 'q-spd-2o1',
        text: {
          en: '500 kg',
          nl: '500 kg',
          ar: '500 كجم'
        }
      },
      {
        id: 'q-spd-2o2',
        text: {
          en: '750 kg',
          nl: '750 kg',
          ar: '750 كجم'
        }
      },
      {
        id: 'q-spd-2o3',
        text: {
          en: '1000 kg',
          nl: '1000 kg',
          ar: '1000 كجم'
        }
      },
      {
        id: 'q-spd-2o4',
        text: {
          en: '1500 kg',
          nl: '1500 kg',
          ar: '1500 كجم'
        }
      }
    ],
    correctAnswerId: 'q-spd-2o2',
    explanation: {
      en: 'An unbraked trailer (one without its own braking system) may not exceed 750 kg maximum permitted mass. Above this, the trailer must have its own brakes. (Voertuigreglement art. 5.1.44)',
      nl: 'Een aanhanger zonder eigen remsysteem mag een maximale toegestane massa van 750 kg niet overschrijden. Daarboven moet de aanhanger over eigen remmen beschikken. (Voertuigreglement art. 5.1.44)',
      ar: 'لا يجوز أن تتجاوز المقطورة غير المكبحة (التي لا تمتلك نظام فرملة خاصا) الكتلة المسموح بها البالغة 750 كجم. وفوق هذا الحد، يجب أن تكون للمقطورة فرامل خاصة بها. (Voertuigreglement المادة 5.1.44)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-3',
    text: {
      en: 'A load extends more than 1 metre beyond the rear of your vehicle. What must you do?',
      nl: 'Een lading steekt meer dan 1 meter uit achter je voertuig. Wat moet je doen?',
      ar: 'تمتد الحمولة أكثر من متر واحد خلف مركبتك. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-spd-3o1',
        text: {
          en: 'Attach a white reflector to the end of the load',
          nl: 'Bevestig een witte reflector aan het uiteinde van de lading',
          ar: 'ثبت عاكسا أبيض في نهاية الحمولة'
        }
      },
      {
        id: 'q-spd-3o2',
        text: {
          en: 'Mark the end of the load with a red/white striped board and a red reflector',
          nl: 'Markeer het uiteinde van de lading met een rood-wit gestreept bord en een rode reflector',
          ar: 'ضع في نهاية الحمولة لوحة مخططة باللون الأحمر والأبيض وعاكسا أحمر'
        }
      },
      {
        id: 'q-spd-3o3',
        text: {
          en: 'No marking is required for loads under 3 metres overhang',
          nl: 'Er is geen markering vereist voor lading die minder dan 3 meter uitsteekt',
          ar: 'لا يلزم وضع أي علامة للحمولات التي يقل بروزها عن 3 أمتار'
        }
      },
      {
        id: 'q-spd-3o4',
        text: {
          en: 'Drive only during daylight hours',
          nl: 'Rij alleen overdag',
          ar: 'القيادة فقط خلال ساعات النهار'
        }
      }
    ],
    correctAnswerId: 'q-spd-3o2',
    explanation: {
      en: 'If a load extends more than 1 metre beyond the rear of the vehicle, the protruding end must be marked with a red/white striped board (chevron board) and a red reflector, visible from behind. At night or in poor visibility, a red light is also required. (RVV 1990 art. 76)',
      nl: 'Als een lading meer dan 1 meter achter het voertuig uitsteekt, moet het uitstekende uiteinde worden gemarkeerd met een rood-wit gestreept bord (gevaarbord) en een rode reflector, zichtbaar van achteren. Bij nacht of slecht zicht is ook een rood licht vereist. (RVV 1990 art. 76)',
      ar: 'إذا امتدت الحمولة أكثر من متر واحد خلف المركبة، يجب وضع علامة على الطرف البارز بلوحة مخططة باللون الأحمر والأبيض وعاكس أحمر مرئي من الخلف. في الليل أو عند ضعف الرؤية، يلزم أيضا ضوء أحمر. (RVV 1990 المادة 76)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-4',
    text: {
      en: 'Are passengers allowed to travel inside a closed trailer being towed on a public road?',
      nl: 'Mogen passagiers meerijden in een gesloten aanhanger die op de openbare weg wordt gesleept?',
      ar: 'هل يسمح للركاب بالسفر داخل مقطورة مغلقة يتم سحبها على الطريق العام؟'
    },
    options: [
      {
        id: 'q-spd-4o1',
        text: {
          en: 'Yes, if they are seated and belted',
          nl: 'Ja, als ze zitten en een veiligheidsgordel dragen',
          ar: 'نعم، إذا كانوا جالسين وربطوا حزام الأمان'
        }
      },
      {
        id: 'q-spd-4o2',
        text: {
          en: 'Yes, but only on roads with a speed limit of 30 km/h or less',
          nl: 'Ja, maar alleen op wegen met een maximumsnelheid van 30 km/u of minder',
          ar: 'نعم، ولكن فقط على الطرق ذات حد السرعة 30 كم/ساعة أو أقل'
        }
      },
      {
        id: 'q-spd-4o3',
        text: {
          en: 'No, passengers are never permitted in trailers',
          nl: 'Nee, passagiers zijn nooit toegestaan in aanhangers',
          ar: 'لا، لا يسمح أبدا للركاب بالسفر في المقطورات'
        }
      },
      {
        id: 'q-spd-4o4',
        text: {
          en: 'Yes, if the trailer has windows',
          nl: 'Ja, als de aanhanger ramen heeft',
          ar: 'نعم، إذا كانت المقطورة تحتوي على نوافذ'
        }
      }
    ],
    correctAnswerId: 'q-spd-4o3',
    explanation: {
      en: 'Passengers are never permitted to travel inside a trailer on a public road. The only occupants allowed in a moving vehicle are those in the designated passenger compartment of the towing vehicle. (RVV 1990 art. 61)',
      nl: 'Passagiers mogen nooit meerijden in een aanhanger op de openbare weg. De enige inzittenden die in een rijdend voertuig mogen zitten, zijn die in het aangewezen passagiersgedeelte van het trekkende voertuig. (RVV 1990 art. 61)',
      ar: 'لا يسمح أبدا للركاب بالسفر داخل مقطورة على الطريق العام. الأشخاص الوحيدون المسموح لهم في مركبة متحركة هم أولئك الموجودون في حجرة الركاب المخصصة في المركبة الجارة. (RVV 1990 المادة 61)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-5',
    text: {
      en: 'Your trailer is wider than your towing vehicle. What is required?',
      nl: 'Je aanhanger is breder dan je trekkende voertuig. Wat is vereist?',
      ar: 'مقطورتك أعرض من مركبتك الجارة. ما الذي يشترط؟'
    },
    options: [
      {
        id: 'q-spd-5o1',
        text: {
          en: 'Nothing — the trailer width makes no difference',
          nl: 'Niets — de breedte van de aanhanger maakt geen verschil',
          ar: 'لا شيء — عرض المقطورة لا يحدث فرقا'
        }
      },
      {
        id: 'q-spd-5o2',
        text: {
          en: 'Fit extended side mirrors on the towing vehicle',
          nl: 'Monteer uitklapbare buitenspiegels op het trekkende voertuig',
          ar: 'ركب مرايا جانبية ممتدة على المركبة الجارة'
        }
      },
      {
        id: 'q-spd-5o3',
        text: {
          en: 'Reduce speed to 60 km/h on all roads',
          nl: 'Verlaag de snelheid naar 60 km/u op alle wegen',
          ar: 'قلل السرعة إلى 60 كم/ساعة على جميع الطرق'
        }
      },
      {
        id: 'q-spd-5o4',
        text: {
          en: 'Attach orange marker lights to the sides of the trailer',
          nl: 'Bevestig oranje markeringslichten aan de zijkanten van de aanhanger',
          ar: 'ثبت أضواء تحديد برتقالية على جانبي المقطورة'
        }
      }
    ],
    correctAnswerId: 'q-spd-5o2',
    explanation: {
      en: 'When a trailer is wider than the towing vehicle, you must fit extended (towing) mirrors so you can see along both sides of the trailer. Without adequate rearward visibility it is illegal to tow. (RVV 1990 art. 78)',
      nl: 'Wanneer een aanhanger breder is dan het trekkende voertuig, moet je uitklapbare (aanhanger)spiegels monteren zodat je langs beide zijkanten van de aanhanger kunt kijken. Zonder voldoende achterwaarts zicht is het illegaal om te slepen. (RVV 1990 art. 78)',
      ar: 'عندما تكون المقطورة أعرض من المركبة الجارة، يجب تركيب مرايا ممتدة حتى تتمكن من رؤية كلا جانبي المقطورة. بدون رؤية خلفية كافية، يعد السحب مخالفا للقانون. (RVV 1990 المادة 78)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-6',
    text: {
      en: 'While towing a trailer at 80 km/h, the trailer begins to sway from side to side. What should you do?',
      nl: 'Terwijl je met 80 km/u een aanhanger trekt, begint de aanhanger van links naar rechts te slingeren. Wat moet je doen?',
      ar: 'أثناء سحب مقطورة بسرعة 80 كم/ساعة، تبدأ المقطورة في الاهتزاز من جانب إلى آخر. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-spd-6o1',
        text: {
          en: 'Brake hard to stop the swaying immediately',
          nl: 'Rem hard om het slingeren onmiddellijk te stoppen',
          ar: 'اكبح بقوة لإيقاف الاهتزاز فورا'
        }
      },
      {
        id: 'q-spd-6o2',
        text: {
          en: 'Steer sharply in the opposite direction to counteract the sway',
          nl: 'Stuur scherp in de tegengestelde richting om het slingeren tegen te gaan',
          ar: 'أدر المقود بحدة في الاتجاه المعاكس لمواجهة الاهتزاز'
        }
      },
      {
        id: 'q-spd-6o3',
        text: {
          en: 'Ease off the accelerator gradually and hold the steering wheel firmly — do not brake hard',
          nl: 'Laat het gaspedaal geleidelijk los en houd het stuur stevig vast — rem niet hard',
          ar: 'أفلت دواسة الوقود تدريجيا وأمسك المقود بإحكام — لا تكبح بقوة'
        }
      },
      {
        id: 'q-spd-6o4',
        text: {
          en: 'Accelerate to drive out of the sway',
          nl: 'Versnel om uit het slingeren te rijden',
          ar: 'أسرع للخروج من حالة الاهتزاز'
        }
      }
    ],
    correctAnswerId: 'q-spd-6o3',
    explanation: {
      en: 'When a trailer sways (snakes), gently ease off the accelerator and grip the steering wheel firmly — do not steer sharply or brake hard. Braking hard or sudden steering can cause a jackknife or rollover. Slowing gradually allows the trailer to stabilise.',
      nl: 'Wanneer een aanhanger begint te slingeren, laat je het gaspedaal voorzichtig los en houd je het stuur stevig vast — stuur niet scherp en rem niet hard. Hard remmen of plotseling sturen kan leiden tot een harmonicastoring of kanteling. Geleidelijk afremmen stelt de aanhanger in staat te stabiliseren.',
      ar: 'عندما تبدأ المقطورة في الاهتزاز، أفلت دواسة الوقود برفق وأمسك المقود بإحكام — لا تدر المقود بحدة ولا تكبح بقوة. الكبح الشديد أو التوجيه المفاجئ قد يتسبب في طي المقطورة أو انقلابها. التباطؤ التدريجي يتيح للمقطورة أن تستقر.'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-7',
    text: {
      en: 'Which number plate must a trailer display?',
      nl: 'Welk kenteken moet een aanhanger voeren?',
      ar: 'ما لوحة الترقيم التي يجب أن تعرضها المقطورة؟'
    },
    options: [
      {
        id: 'q-spd-7o1',
        text: {
          en: 'Its own separate registered plate',
          nl: 'Een eigen geregistreerd kenteken',
          ar: 'لوحتها الخاصة المسجلة'
        }
      },
      {
        id: 'q-spd-7o2',
        text: {
          en: 'The same plate as the towing vehicle',
          nl: 'Hetzelfde kenteken als het trekkende voertuig',
          ar: 'نفس لوحة المركبة الجارة'
        }
      },
      {
        id: 'q-spd-7o3',
        text: {
          en: 'No plate is required for trailers under 750 kg',
          nl: 'Er is geen kenteken vereist voor aanhangers onder de 750 kg',
          ar: 'لا تحتاج المقطورات التي تقل عن 750 كجم إلى لوحة ترقيم'
        }
      },
      {
        id: 'q-spd-7o4',
        text: {
          en: 'A temporary paper plate issued by the RDW',
          nl: 'Een tijdelijk papieren kenteken afgegeven door de RDW',
          ar: 'لوحة ورقية مؤقتة صادرة عن RDW'
        }
      }
    ],
    correctAnswerId: 'q-spd-7o2',
    explanation: {
      en: 'In the Netherlands, a trailer must display the same registration plate as the towing vehicle. The plate is mounted at the rear of the trailer and must be illuminated at night. (Voertuigreglement)',
      nl: 'In Nederland moet een aanhanger hetzelfde kenteken voeren als het trekkende voertuig. Het kenteken wordt achteraan de aanhanger gemonteerd en moet bij nacht verlicht zijn. (Voertuigreglement)',
      ar: 'في هولندا، يجب أن تعرض المقطورة نفس لوحة ترقيم المركبة الجارة. تركب اللوحة في مؤخرة المقطورة ويجب إضاءتها في الليل. (Voertuigreglement)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-8',
    text: {
      en: 'How should you load a trailer to ensure stable towing?',
      nl: 'Hoe moet je een aanhanger beladen om stabiel te kunnen trekken?',
      ar: 'كيف ينبغي لك تحميل المقطورة لضمان سحب مستقر؟'
    },
    options: [
      {
        id: 'q-spd-8o1',
        text: {
          en: 'Place the heaviest items at the rear of the trailer',
          nl: 'Leg de zwaarste spullen achterin de aanhanger',
          ar: 'ضع أثقل العناصر في مؤخرة المقطورة'
        }
      },
      {
        id: 'q-spd-8o2',
        text: {
          en: 'Place the heaviest items at the front (above the axle) and keep the load centred',
          nl: 'Leg de zwaarste spullen vooraan (boven de as) en houd de lading gecentreerd',
          ar: 'ضع أثقل العناصر في المقدمة (فوق المحور) وابق الحمولة في المركز'
        }
      },
      {
        id: 'q-spd-8o3',
        text: {
          en: 'Distribute the load evenly from side to side only — front-rear position does not matter',
          nl: 'Verdeel de lading alleen gelijkmatig van links naar rechts — de voor-achterpositie maakt niet uit',
          ar: 'وزع الحمولة بالتساوي من جانب إلى جانب فقط — الموضع الأمامي الخلفي لا يهم'
        }
      },
      {
        id: 'q-spd-8o4',
        text: {
          en: 'Load does not affect stability as long as the total weight is within limits',
          nl: 'De lading heeft geen invloed op de stabiliteit zolang het totaalgewicht binnen de limieten blijft',
          ar: 'لا تؤثر الحمولة على الاستقرار طالما أن الوزن الإجمالي ضمن الحدود'
        }
      }
    ],
    correctAnswerId: 'q-spd-8o2',
    explanation: {
      en: 'The heaviest items should be placed over or just in front of the trailer axle, centred side-to-side. This keeps downward pressure on the tow ball (noseweight) within the recommended range (typically 25–75 kg) and prevents the trailer from swaying.',
      nl: 'De zwaarste spullen moeten boven of net voor de aanhangeras worden geplaatst, gecentreerd van links naar rechts. Dit houdt de neerwaartse druk op de trekhaak (steunlast) binnen het aanbevolen bereik (doorgaans 25–75 kg) en voorkomt slingeren van de aanhanger.',
      ar: 'يجب وضع أثقل العناصر فوق محور المقطورة أو أمامه مباشرة، ومتمركزة من جانب إلى جانب. يبقي هذا الضغط النازل على الكرة الجارة (الوزن الأنفي) ضمن النطاق الموصى به (عادة 25–75 كجم) ويمنع اهتزاز المقطورة.'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-9',
    text: {
      en: 'What lights must a trailer have when driving on a public road at night?',
      nl: 'Welke verlichting moet een aanhanger hebben bij nachtelijk rijden op de openbare weg?',
      ar: 'ما الأضواء التي يجب أن تمتلكها المقطورة عند القيادة على الطريق العام ليلا؟'
    },
    options: [
      {
        id: 'q-spd-9o1',
        text: {
          en: 'Only rear reflectors — no active lights required',
          nl: 'Alleen achterreflectoren — geen actieve verlichting vereist',
          ar: 'عاكسات خلفية فقط — لا تلزم أضواء فعالة'
        }
      },
      {
        id: 'q-spd-9o2',
        text: {
          en: 'Rear position lights, brake lights, indicators and number plate light',
          nl: 'Achterpositielichten, remlichten, richtingaanwijzers en kentekenverlichting',
          ar: 'أضواء الموضع الخلفية وأضواء الفرامل والإشارات وضوء لوحة الترقيم'
        }
      },
      {
        id: 'q-spd-9o3',
        text: {
          en: 'Rear position lights only',
          nl: 'Alleen achterpositielichten',
          ar: 'أضواء الموضع الخلفية فقط'
        }
      },
      {
        id: 'q-spd-9o4',
        text: {
          en: 'The trailer uses the towing vehicle lights — no separate lights needed',
          nl: 'De aanhanger gebruikt de verlichting van het trekkende voertuig — geen aparte verlichting nodig',
          ar: 'تستخدم المقطورة أضواء المركبة الجارة — لا تلزم أضواء منفصلة'
        }
      }
    ],
    correctAnswerId: 'q-spd-9o2',
    explanation: {
      en: 'A trailer must have functioning rear position lights (tail lights), brake lights, direction indicators, a number plate light, and red rear reflectors. These are required by law and must be connected and operational before driving. (Voertuigreglement art. 3.7)',
      nl: 'Een aanhanger moet beschikken over werkende achterpositielichten (achterlichten), remlichten, richtingaanwijzers, kentekenverlichting en rode achterreflectoren. Dit is wettelijk verplicht en alles moet aangesloten en operationeel zijn voordat je gaat rijden. (Voertuigreglement art. 3.7)',
      ar: 'يجب أن تمتلك المقطورة أضواء موضع خلفية عاملة (أضواء ذيل)، وأضواء فرامل، وأضواء اتجاه، وضوء لوحة ترقيم، وعاكسات حمراء خلفية. هذه متطلبات قانونية ويجب توصيلها وتشغيلها قبل القيادة. (Voertuigreglement المادة 3.7)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-10',
    text: {
      en: 'What is the maximum load overhang permitted at the front of a vehicle?',
      nl: 'Wat is de maximaal toegestane uitsteek van de lading aan de voorzijde van een voertuig?',
      ar: 'ما هو الحد الأقصى لبروز الحمولة المسموح به في مقدمة المركبة؟'
    },
    options: [
      {
        id: 'q-spd-10o1',
        text: {
          en: '0.5 metres',
          nl: '0,5 meter',
          ar: '0.5 متر'
        }
      },
      {
        id: 'q-spd-10o2',
        text: {
          en: '1 metre',
          nl: '1 meter',
          ar: '1 متر'
        }
      },
      {
        id: 'q-spd-10o3',
        text: {
          en: '2 metres',
          nl: '2 meter',
          ar: '2 متر'
        }
      },
      {
        id: 'q-spd-10o4',
        text: {
          en: 'There is no front overhang limit',
          nl: 'Er is geen limiet voor uitsteek aan de voorzijde',
          ar: 'لا يوجد حد لبروز الحمولة من الأمام'
        }
      }
    ],
    correctAnswerId: 'q-spd-10o2',
    explanation: {
      en: 'A load may not protrude more than 1 metre beyond the front of the vehicle. If it does, special permits and markings are required. The rear overhang limit without special permits is 3 metres beyond the rear of the vehicle. (RVV 1990 art. 76)',
      nl: 'Een lading mag niet meer dan 1 meter voor het voertuig uitsteken. Als dat het geval is, zijn speciale vergunningen en markeringen vereist. De achterwaartse uitsteeklimiet zonder speciale vergunningen is 3 meter achter het voertuig. (RVV 1990 art. 76)',
      ar: 'لا يجوز أن تبرز الحمولة أكثر من متر واحد أمام المركبة. إذا تجاوزت ذلك، تلزم تصاريح خاصة وعلامات تحذيرية. الحد الأقصى للبروز الخلفي دون تصاريح خاصة هو 3 أمتار خلف المركبة. (RVV 1990 المادة 76)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-11',
    text: {
      en: 'You are transporting an unsecured load of loose gravel in an open trailer. What is the risk and what must you do?',
      nl: 'Je vervoert een niet-vastgezette lading los grind in een open aanhanger. Wat is het risico en wat moet je doen?',
      ar: 'أنت تنقل حمولة غير مثبتة من الحصى السائب في مقطورة مفتوحة. ما هو الخطر وماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-spd-11o1',
        text: {
          en: 'No risk — small loose material does not need securing',
          nl: 'Geen risico — klein los materiaal hoeft niet vastgezet te worden',
          ar: 'لا خطر — المواد السائبة الصغيرة لا تحتاج إلى تثبيت'
        }
      },
      {
        id: 'q-spd-11o2',
        text: {
          en: 'Cover the load with a tarpaulin to prevent material from blowing onto the road',
          nl: 'Dek de lading af met een dekzeil om te voorkomen dat materiaal op de weg waait',
          ar: 'غط الحمولة بقماش مشمع لمنع انتشار المواد على الطريق'
        }
      },
      {
        id: 'q-spd-11o3',
        text: {
          en: 'Drive only on roads with a 30 km/h limit',
          nl: 'Rij alleen op wegen met een limiet van 30 km/u',
          ar: 'القيادة فقط على الطرق ذات حد السرعة 30 كم/ساعة'
        }
      },
      {
        id: 'q-spd-11o4',
        text: {
          en: 'Fill the trailer no more than halfway',
          nl: 'Vul de aanhanger niet meer dan halfvol',
          ar: 'لا تملأ المقطورة أكثر من نصفها'
        }
      }
    ],
    correctAnswerId: 'q-spd-11o2',
    explanation: {
      en: 'Loose loads such as gravel, sand, or debris must be covered (e.g. with a tarpaulin or net) to prevent material falling onto the road. Material falling from vehicles creates a serious hazard for other road users and can result in fines. (Wegenverkeerswet art. 5)',
      nl: 'Losse ladingen zoals grind, zand of puin moeten worden afgedekt (bijv. met een dekzeil of net) om te voorkomen dat materiaal op de weg valt. Materiaal dat van voertuigen valt, vormt een ernstig gevaar voor andere weggebruikers en kan leiden tot boetes. (Wegenverkeerswet art. 5)',
      ar: 'يجب تغطية الأحمال السائبة مثل الحصى والرمل والحطام (مثلا بقماش مشمع أو شبكة) لمنع سقوط المواد على الطريق. المواد المتساقطة من المركبات تشكل خطرا جسيما على مستخدمي الطريق الآخرين وقد تؤدي إلى غرامات. (Wegenverkeerswet المادة 5)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-12',
    text: {
      en: 'What is the maximum width of a trailer or load permitted on Dutch roads without a special permit?',
      nl: 'Wat is de maximaal toegestane breedte van een aanhanger of lading op Nederlandse wegen zonder speciale vergunning?',
      ar: 'ما هو الحد الأقصى لعرض المقطورة أو الحمولة المسموح به على الطرق الهولندية دون تصريح خاص؟'
    },
    options: [
      {
        id: 'q-spd-12o1',
        text: {
          en: '2.00 metres',
          nl: '2,00 meter',
          ar: '2.00 متر'
        }
      },
      {
        id: 'q-spd-12o2',
        text: {
          en: '2.30 metres',
          nl: '2,30 meter',
          ar: '2.30 متر'
        }
      },
      {
        id: 'q-spd-12o3',
        text: {
          en: '2.55 metres',
          nl: '2,55 meter',
          ar: '2.55 متر'
        }
      },
      {
        id: 'q-spd-12o4',
        text: {
          en: '3.00 metres',
          nl: '3,00 meter',
          ar: '3.00 متر'
        }
      }
    ],
    correctAnswerId: 'q-spd-12o3',
    explanation: {
      en: 'The maximum permitted width for a trailer or load without a special exemption permit is 2.55 metres. Loads or trailers wider than this require a special permit, escort vehicles, and specific route planning. (Voertuigreglement art. 5.2.1)',
      nl: 'De maximaal toegestane breedte van een aanhanger of lading zonder speciale ontheffing is 2,55 meter. Ladingen of aanhangers die breder zijn, vereisen een speciale vergunning, begeleidingsvoertuigen en specifieke routeplanning. (Voertuigreglement art. 5.2.1)',
      ar: 'الحد الأقصى المسموح به لعرض مقطورة أو حمولة دون تصريح إعفاء خاص هو 2.55 متر. تتطلب الأحمال أو المقطورات الأوسع من ذلك تصريحا خاصا ومركبات مرافقة وتخطيطا محددا للمسار. (Voertuigreglement المادة 5.2.1)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-13',
    text: {
      en: 'You park and disconnect your trailer on a slope. What must you do before unhitching?',
      nl: 'Je parkeert en ontkoppelt je aanhanger op een helling. Wat moet je doen voordat je loskoppelt?',
      ar: 'تقوم بركن وفصل مقطورتك على منحدر. ماذا يجب أن تفعل قبل فك الارتباط؟'
    },
    options: [
      {
        id: 'q-spd-13o1',
        text: {
          en: 'Nothing — the tow ball coupling is sufficient to hold it',
          nl: 'Niets — de trekhaaakkoppeling is voldoende om hem vast te houden',
          ar: 'لا شيء — كبة الاقتران كافية للإمساك بها'
        }
      },
      {
        id: 'q-spd-13o2',
        text: {
          en: 'Apply the trailer handbrake and place wheel chocks under the wheels',
          nl: 'Zet de handrem van de aanhanger aan en plaats wielwiggen onder de wielen',
          ar: 'طبق الفرامل اليدوية للمقطورة وضع إسفين العجلات تحت العجلات'
        }
      },
      {
        id: 'q-spd-13o3',
        text: {
          en: 'Lower the jockey wheel only',
          nl: 'Laat alleen het steunwiel zakken',
          ar: 'أنزل عجلة الدعم فقط'
        }
      },
      {
        id: 'q-spd-13o4',
        text: {
          en: 'Leave the vehicle in reverse gear as a brake',
          nl: 'Laat het voertuig in de achteruit staan als rem',
          ar: 'اترك المركبة في الرجوع للخلف كفرامل'
        }
      }
    ],
    correctAnswerId: 'q-spd-13o2',
    explanation: {
      en: 'Before unhitching a trailer on a slope, apply the trailer handbrake and place wheel chocks (wiggen) under the wheels to prevent it rolling away. Relying on the coupling alone is dangerous — an uncoupled trailer can roll and cause serious accidents.',
      nl: 'Voordat je een aanhanger op een helling loskoppelt, zet je de handrem van de aanhanger aan en plaats je wielwiggen onder de wielen om te voorkomen dat hij wegrijdt. Alleen op de koppeling vertrouwen is gevaarlijk — een losgekoppelde aanhanger kan wegrijden en ernstige ongelukken veroorzaken.',
      ar: 'قبل فك ارتباط مقطورة على منحدر، طبق الفرامل اليدوية للمقطورة وضع إسفين العجلات تحت العجلات لمنعها من الدحرجة. الاعتماد على الاقتران وحده أمر خطير — يمكن لمقطورة مفصولة أن تتدحرج وتسبب حوادث خطيرة.'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-14',
    text: {
      en: 'When reversing with a trailer, which way do you turn the steering wheel to make the trailer go right?',
      nl: 'Wanneer je achteruitrijdt met een aanhanger, welke kant stuur je dan op om de aanhanger naar rechts te laten gaan?',
      ar: 'عند التراجع بمقطورة، في أي اتجاه تدير عجلة القيادة لتحريك المقطورة إلى اليمين؟'
    },
    options: [
      {
        id: 'q-spd-14o1',
        text: {
          en: 'Turn right — the same direction as when driving forward',
          nl: 'Naar rechts sturen — dezelfde richting als bij vooruitrijden',
          ar: 'أدر إلى اليمين — نفس الاتجاه عند القيادة للأمام'
        }
      },
      {
        id: 'q-spd-14o2',
        text: {
          en: 'Turn left — opposite to the direction you want the trailer to go',
          nl: 'Naar links sturen — tegengesteld aan de richting waarin je de aanhanger wilt laten gaan',
          ar: 'أدر إلى اليسار — عكس الاتجاه الذي تريد أن تتجه إليه المقطورة'
        }
      },
      {
        id: 'q-spd-14o3',
        text: {
          en: 'The trailer always follows the vehicle automatically',
          nl: 'De aanhanger volgt het voertuig altijd automatisch',
          ar: 'تتبع المقطورة دائما المركبة تلقائيا'
        }
      },
      {
        id: 'q-spd-14o4',
        text: {
          en: 'Keep the wheel straight and use brakes to steer',
          nl: 'Houd het stuur recht en gebruik de remmen om te sturen',
          ar: 'أبق العجلة مستقيمة واستخدم الفرامل للتوجيه'
        }
      }
    ],
    correctAnswerId: 'q-spd-14o2',
    explanation: {
      en: 'When reversing with a trailer, the trailer moves in the opposite direction to the steering wheel. To make the trailer go right, you turn the steering wheel left. This is the opposite of normal driving and requires practice. Make small steering corrections to avoid jackknifing.',
      nl: 'Bij achteruitrijden met een aanhanger beweegt de aanhanger in de tegengestelde richting van het stuur. Om de aanhanger naar rechts te laten gaan, stuur je naar links. Dit is het tegenovergestelde van normaal rijden en vereist oefening. Maak kleine stuurcorrecties om harmonicastoring te voorkomen.',
      ar: 'عند التراجع بمقطورة، تتحرك المقطورة في الاتجاه المعاكس لعجلة القيادة. لتحريك المقطورة إلى اليمين، تدير عجلة القيادة إلى اليسار. هذا عكس القيادة الطبيعية ويتطلب تدريبا. أجر تصحيحات توجيه صغيرة لتجنب طي المقطورة.'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-15',
    text: {
      en: "You want to tow a caravan. The caravan's maximum permitted mass is 1,200 kg and it has its own brakes. What is the maximum speed on a 100 km/h road outside a built-up area?",
      nl: 'Je wilt een caravan trekken. De maximale toegestane massa van de caravan is 1.200 kg en hij heeft eigen remmen. Wat is de maximumsnelheid op een weg met een limiet van 100 km/u buiten de bebouwde kom?',
      ar: 'تريد سحب قافلة. الكتلة المسموح بها للقافلة هي 1,200 كجم ولها فرامل خاصة. ما هي السرعة القصوى على طريق بحد 100 كم/ساعة خارج المنطقة العمرانية؟'
    },
    options: [
      {
        id: 'q-spd-15o1',
        text: {
          en: '100 km/h',
          nl: '100 km/u',
          ar: '100 كم/ساعة'
        }
      },
      {
        id: 'q-spd-15o2',
        text: {
          en: '90 km/h',
          nl: '90 km/u',
          ar: '90 كم/ساعة'
        }
      },
      {
        id: 'q-spd-15o3',
        text: {
          en: '80 km/h',
          nl: '80 km/u',
          ar: '80 كم/ساعة'
        }
      },
      {
        id: 'q-spd-15o4',
        text: {
          en: '70 km/h',
          nl: '70 km/u',
          ar: '70 كم/ساعة'
        }
      }
    ],
    correctAnswerId: 'q-spd-15o2',
    explanation: {
      en: 'When towing any trailer or caravan with a category B licence, the maximum speed outside built-up areas (including roads with a 100 km/h limit) is 90 km/h. This applies regardless of whether the trailer has brakes or what the general road limit is. (RVV 1990 art. 22)',
      nl: 'Als je met een categorie B-rijbewijs een aanhanger of caravan trekt, geldt buiten de bebouwde kom (ook op wegen met een limiet van 100 km/u) een maximumsnelheid van 90 km/u. Dit geldt ongeacht of de aanhanger remmen heeft of wat de algemene weglimiet is. (RVV 1990 art. 22)',
      ar: 'عند سحب أي مقطورة أو قافلة بترخيص الفئة B، تكون السرعة القصوى خارج المناطق العمرانية (بما في ذلك الطرق ذات الحد الأقصى 100 كم/ساعة) هي 90 كم/ساعة. ينطبق هذا بغض النظر عما إذا كانت للمقطورة فرامل أو ما هو الحد العام للطريق. (RVV 1990 المادة 22)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-16',
    text: {
      en: 'Which of the following correctly describes the safety chain (or breakaway cable) on a trailer?',
      nl: 'Welke van de volgende beschrijvingen geeft de veiligheidsketting (of losrijkabel) op een aanhanger correct weer?',
      ar: 'أي مما يلي يصف بشكل صحيح سلسلة الأمان (أو كابل الانفصال) على المقطورة؟'
    },
    options: [
      {
        id: 'q-spd-16o1',
        text: {
          en: 'It is optional and only needed on trailers over 1,500 kg',
          nl: 'Hij is optioneel en alleen nodig op aanhangers boven 1.500 kg',
          ar: 'إنه اختياري وضروري فقط للمقطورات التي تزيد عن 1,500 كجم'
        }
      },
      {
        id: 'q-spd-16o2',
        text: {
          en: 'It connects the trailer brakes to the towing vehicle so the trailer brakes apply automatically if it becomes uncoupled',
          nl: 'Hij verbindt de remmen van de aanhanger met het trekkende voertuig, zodat de remmen van de aanhanger automatisch worden geactiveerd als de koppeling losschiet',
          ar: 'يربط فرامل المقطورة بالمركبة الجارة بحيث تعمل فرامل المقطورة تلقائيا إذا انفصلت'
        }
      },
      {
        id: 'q-spd-16o3',
        text: {
          en: 'It prevents the trailer from swaying at high speed',
          nl: 'Hij voorkomt dat de aanhanger slingert bij hoge snelheid',
          ar: 'يمنع المقطورة من الاهتزاز عند السرعات العالية'
        }
      },
      {
        id: 'q-spd-16o4',
        text: {
          en: 'It is a decorative chain with no safety function',
          nl: 'Het is een decoratieve ketting zonder veiligheidsfunctie',
          ar: 'إنها سلسلة زخرفية بدون وظيفة أمان'
        }
      }
    ],
    correctAnswerId: 'q-spd-16o2',
    explanation: {
      en: "The breakaway cable (losrijkabel) connects the trailer's handbrake mechanism to the towing vehicle. If the trailer accidentally uncouples, the cable pulls the trailer handbrake on, slowing the trailer and preventing a runaway. It is required on braked trailers. (Voertuigreglement)",
      nl: 'De losrijkabel verbindt het handremgechanisme van de aanhanger met het trekkende voertuig. Als de aanhanger per ongeluk loskoppelt, trekt de kabel de handrem van de aanhanger aan, waardoor de aanhanger vertraagt en een ongecontroleerde situatie wordt voorkomen. Hij is verplicht op aanhangers met remmen. (Voertuigreglement)',
      ar: 'تربط الكابل الانفصالي (losrijkabel) آلية الفرامل اليدوية للمقطورة بالمركبة الجارة. إذا انفصلت المقطورة عن غير قصد، تشد الكابل الفرامل اليدوية للمقطورة، مما يبطئ المقطورة ويمنع انفلاتها. إنه مطلوب في المقطورات المكبحة. (Voertuigreglement)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-17',
    text: {
      en: 'You are carrying a long ladder on your roof rack. It extends 1.5 metres beyond the rear of your car. What marking is required?',
      nl: 'Je vervoert een lange ladder op je dakdrager. Hij steekt 1,5 meter uit achter je auto. Welke markering is vereist?',
      ar: 'تحمل سلما طويلا على حامل السقف. يمتد 1.5 متر خلف سيارتك. ما هي العلامة المطلوبة؟'
    },
    options: [
      {
        id: 'q-spd-17o1',
        text: {
          en: 'A red flag only',
          nl: 'Alleen een rode vlag',
          ar: 'علم أحمر فقط'
        }
      },
      {
        id: 'q-spd-17o2',
        text: {
          en: 'A red/white striped board with a red reflector at the end',
          nl: 'Een rood-wit gestreept bord met een rode reflector aan het uiteinde',
          ar: 'لوحة مخططة باللون الأحمر والأبيض مع عاكس أحمر في النهاية'
        }
      },
      {
        id: 'q-spd-17o3',
        text: {
          en: 'Yellow flashing lights on the roof',
          nl: 'Gele knipperlichten op het dak',
          ar: 'أضواء وميض صفراء على السقف'
        }
      },
      {
        id: 'q-spd-17o4',
        text: {
          en: 'No marking needed below 2 metres overhang',
          nl: 'Geen markering nodig bij uitsteek onder 2 meter',
          ar: 'لا تلزم علامة عند بروز أقل من متري'
        }
      }
    ],
    correctAnswerId: 'q-spd-17o2',
    explanation: {
      en: 'Any load protruding more than 1 metre beyond the rear of the vehicle must be marked with a red/white striped board (gevaarbord) and a red reflector at the very end. At night, a red light is also required. This warns following traffic of the hazard. (RVV 1990 art. 76)',
      nl: 'Elke lading die meer dan 1 meter achter het voertuig uitsteekt, moet worden gemarkeerd met een rood-wit gestreept bord (gevaarbord) en een rode reflector aan het uiteinde. Bij nacht is ook een rood licht vereist. Dit waarschuwt het achterliggende verkeer voor het gevaar. (RVV 1990 art. 76)',
      ar: 'يجب وضع علامة على أي حمولة تبرز أكثر من متر واحد خلف المركبة بلوحة مخططة باللون الأحمر والأبيض (gevaarbord) وعاكس أحمر في نهايتها. في الليل، يلزم أيضا ضوء أحمر. هذا يحذر حركة المرور القادمة من الخلف من الخطر. (RVV 1990 المادة 76)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-18',
    text: {
      en: "What does a vehicle's \"towing capacity\" (trekgewicht) mean?",
      nl: 'Wat betekent het "trekgewicht" van een voertuig?',
      ar: 'ماذا تعني "قدرة الجر" (trekgewicht) للمركبة؟'
    },
    options: [
      {
        id: 'q-spd-18o1',
        text: {
          en: "The maximum weight the vehicle's roof rack can carry",
          nl: 'Het maximale gewicht dat de dakdrager van het voertuig kan dragen',
          ar: 'الحد الأقصى للوزن الذي يمكن لحامل سقف المركبة حمله'
        }
      },
      {
        id: 'q-spd-18o2',
        text: {
          en: 'The maximum permitted mass of a trailer that the vehicle is approved to tow',
          nl: 'De maximaal toegestane massa van een aanhanger die het voertuig is goedgekeurd om te trekken',
          ar: 'الحد الأقصى للكتلة المسموح بها للمقطورة التي تمت الموافقة على سحبها بالمركبة'
        }
      },
      {
        id: 'q-spd-18o3',
        text: {
          en: 'The combined weight of vehicle and trailer when fully loaded',
          nl: 'Het gecombineerde gewicht van het voertuig en de aanhanger bij volle belading',
          ar: 'الوزن المجمع للمركبة والمقطورة عند التحميل الكامل'
        }
      },
      {
        id: 'q-spd-18o4',
        text: {
          en: 'The maximum load you can carry inside the vehicle',
          nl: 'De maximale lading die je in het voertuig kunt vervoeren',
          ar: 'الحد الأقصى للحمولة التي يمكنك حملها داخل المركبة'
        }
      }
    ],
    correctAnswerId: 'q-spd-18o2',
    explanation: {
      en: "The towing capacity (trekgewicht) is the maximum permitted trailer mass the vehicle is engineered and approved to tow. This figure is stated in the vehicle's registration document (kentekenbewijs). Exceeding it is illegal and dangerous — it affects braking, stability, and tyre load.",
      nl: 'Het trekgewicht is de maximaal toegestane aanhangermassa waarvoor het voertuig is gebouwd en goedgekeurd om te trekken. Dit getal staat vermeld in het kentekenbewijs van het voertuig. Het overschrijden ervan is illegaal en gevaarlijk — het beïnvloedt het remvermogen, de stabiliteit en de bandbelasting.',
      ar: 'قدرة الجر (trekgewicht) هي الحد الأقصى لكتلة المقطورة المسموح بها التي صممت المركبة وووفق عليها لسحبها. يذكر هذا الرقم في وثيقة تسجيل المركبة (kentekenbewijs). تجاوزه أمر غير قانوني وخطير — فهو يؤثر على الفرملة والاستقرار وحمل الإطارات.'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-19',
    text: {
      en: 'You arrive at a motorway with a trailer. Which lane must you use?',
      nl: 'Je rijdt een snelweg op met een aanhanger. Welke rijstrook moet je gebruiken?',
      ar: 'تصل إلى طريق سريع بمقطورة. أي مسار يجب أن تستخدم؟'
    },
    options: [
      {
        id: 'q-spd-19o1',
        text: {
          en: 'Any lane, including the overtaking lane, as long as you keep to 90 km/h',
          nl: 'Elke rijstrook, inclusief de inhaalrijstrook, zolang je 90 km/u aanhoudt',
          ar: 'أي مسار، بما في ذلك مسار التجاوز، طالما التزمت بـ 90 كم/ساعة'
        }
      },
      {
        id: 'q-spd-19o2',
        text: {
          en: 'The right-hand lane only — you may not use the overtaking lane when towing',
          nl: 'Alleen de rechterrijstrook — je mag de inhaalrijstrook niet gebruiken als je een aanhanger trekt',
          ar: 'المسار الأيمن فقط — لا يجوز لك استخدام مسار التجاوز عند السحب'
        }
      },
      {
        id: 'q-spd-19o3',
        text: {
          en: 'The middle lane to keep distance from merging traffic',
          nl: 'De middelste rijstrook om afstand te houden van invoegend verkeer',
          ar: 'المسار الأوسط للحفاظ على المسافة من حركة المرور المندمجة'
        }
      },
      {
        id: 'q-spd-19o4',
        text: {
          en: 'Any lane except the far-right lane',
          nl: 'Elke rijstrook behalve de meest rechtse rijstrook',
          ar: 'أي مسار باستثناء المسار الأقصى على اليمين'
        }
      }
    ],
    correctAnswerId: 'q-spd-19o2',
    explanation: {
      en: 'When towing a trailer, you must stay in the right-hand lane on a motorway and may not use the overtaking (left) lane. On motorways with three or more lanes, vehicles towing trailers are restricted to the two rightmost lanes. (RVV 1990 art. 12)',
      nl: 'Als je een aanhanger trekt, moet je op de snelweg op de rechterrijstrook blijven en mag je de inhaalrijstrook (linker rijstrook) niet gebruiken. Op snelwegen met drie of meer rijstroken zijn voertuigen met aanhanger beperkt tot de twee meest rechtse rijstroken. (RVV 1990 art. 12)',
      ar: 'عند سحب مقطورة، يجب أن تبقى في المسار الأيمن على الطريق السريع ولا يجوز لك استخدام مسار التجاوز (الأيسر). على الطرق السريعة ذات ثلاثة مسارات أو أكثر، تقتصر المركبات التي تسحب مقطورات على المساري الأيمني. (RVV 1990 المادة 12)'
    },
    subject: 'Specialized Driving'
  },
  {
    id: 'q-spd-20',
    text: {
      en: 'Before setting off with a trailer, you must check the tow ball coupling is correctly locked. How do you verify this?',
      nl: 'Voordat je met een aanhanger vertrekt, moet je controleren of de trekhaaakkoppeling correct vergrendeld is. Hoe verifieer je dit?',
      ar: 'قبل الانطلاق بمقطورة، يجب التحقق من أن كبة الاقتران مقفلة بشكل صحيح. كيف تتحقق من ذلك؟'
    },
    options: [
      {
        id: 'q-spd-20o1',
        text: {
          en: 'Drive slowly for 10 metres — if the trailer stays attached it is secure',
          nl: 'Rijd langzaam 10 meter — als de aanhanger verbonden blijft, is hij veilig',
          ar: 'سر ببطء لمسافة 10 أمتار — إذا بقيت المقطورة متصلة فهي آمنة'
        }
      },
      {
        id: 'q-spd-20o2',
        text: {
          en: 'Lift the trailer jockey wheel to confirm the coupling clamps onto the ball, the locking pin is inserted, and the breakaway cable is attached',
          nl: 'Hef het steunwiel van de aanhanger op om te bevestigen dat de koppeling op de bal klemmt, de vergrendelingspin is aangebracht en de losrijkabel is bevestigd',
          ar: 'ارفع عجلة الدعم للمقطورة للتحقق من أن رأس الاقتران يثبت على الكرة، وأن دبوس القفل مدخل، وأن الكابل الانفصالي متصل'
        }
      },
      {
        id: 'q-spd-20o3',
        text: {
          en: 'Check that the trailer lights work — if they do, the coupling must be secure',
          nl: 'Controleer of de achterlichten van de aanhanger werken — als dat zo is, moet de koppeling veilig zijn',
          ar: 'تحقق من عمل أضواء المقطورة — إذا عملت، فيجب أن يكون الاقتران آمنا'
        }
      },
      {
        id: 'q-spd-20o4',
        text: {
          en: "It is the trailer owner's responsibility, not the driver's",
          nl: 'Het is de verantwoordelijkheid van de aanhangereigenaar, niet van de bestuurder',
          ar: 'إنها مسؤولية مالك المقطورة، وليس السائق'
        }
      }
    ],
    correctAnswerId: 'q-spd-20o2',
    explanation: {
      en: 'Before moving off, physically verify: (1) the coupling head is fully clamped onto the tow ball, (2) the locking pin or safety clip is in place, and (3) the breakaway cable is connected to the towing vehicle. Also raise and secure the jockey wheel. Connecting trailer lights alone does not confirm the coupling is safe.',
      nl: 'Controleer voor het wegrijden fysiek: (1) de koppelkop zit volledig vast op de trekhaak, (2) de vergrendelingspen of veiligheidsclip zit op zijn plaats, en (3) de losrijkabel is verbonden met het trekkende voertuig. Hef ook het steunwiel op en zet het vast. Alleen de achterlichten van de aanhanger aansluiten bevestigt niet dat de koppeling veilig is.',
      ar: 'قبل التحرك، تحقق جسديا من: (1) رأس الاقتران مثبت بالكامل على كبة الجر، (2) دبوس القفل أو مشبك الأمان في مكانه، و(3) الكابل الانفصالي متصل بالمركبة الجارة. ارفع أيضا عجلة الدعم وثبتها. توصيل أضواء المقطورة وحدها لا يؤكد أن الاقتران آمن.'
    },
    subject: 'Specialized Driving'
  }
];
